/**
 * Option calculator engine. Pure functions: no Vue, no I/O.
 * Rates are decimals (0.027 = 2.7%). Business days = Mon-Fri, holidays ignored.
 */

export const ACTION = Object.freeze({ BUY: 0, SELL: 1 })
export const TYPE = Object.freeze({ PUT: 1, CALL: 2 })
export const ANNUAL_BASE = 252

const ATM_THRESHOLD = 0.01
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/

const isPositive = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0
const isNonNegative = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0
const isValidRate = (rate) => typeof rate === 'number' && Number.isFinite(rate)
const hasValue = (object, value) => Object.values(object).includes(value)

/** Normalizes a Date or ISO string to local midnight; null when invalid. */
function toLocalDate (value) {
  if (!value) { return null }
  const iso = typeof value === 'string' ? ISO_DATE.exec(value) : null
  const date = iso
    ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    : new Date(value)
  if (Number.isNaN(date.getTime())) { return null }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/** Counts Mon-Fri days in the interval (start, end]. */
export function businessDays (start, end) {
  const from = toLocalDate(start)
  const to = toLocalDate(end)
  if (!from || !to || to <= from) { return 0 }

  let count = 0
  const cursor = new Date(from)
  cursor.setDate(cursor.getDate() + 1)
  while (cursor <= to) {
    const weekday = cursor.getDay()
    if (weekday !== 0 && weekday !== 6) { count++ }
    cursor.setDate(cursor.getDate() + 1)
  }
  return count
}

/** (1 + rate)^(base / du) - 1 */
export function annualize (rate, du, base = ANNUAL_BASE) {
  if (!isValidRate(rate) || !(du > 0)) { return null }
  return Math.pow(1 + rate, base / du) - 1
}

/** (1 + cdiAnnual)^(du / base) - 1 */
export function cdiPeriod (cdiAnnual, du, base = ANNUAL_BASE) {
  if (!isValidRate(cdiAnnual) || !(du > 0)) { return null }
  return Math.pow(1 + cdiAnnual, du / base) - 1
}

/** Distance of the strike from spot and ATM/ITM/OTM status. */
export function moneyness (type, spot, strike) {
  const distance = (strike - spot) / spot
  if (Math.abs(distance) <= ATM_THRESHOLD) { return { distance, status: 'ATM' } }

  const isItm = type === TYPE.CALL ? spot > strike : spot < strike
  return { distance, status: isItm ? 'ITM' : 'OTM' }
}

export function validateSimulation (input) {
  const errors = []
  if (!input) { return ['Simulation data not provided.'] }

  if (!isPositive(input.spot)) { errors.push('Underlying asset price must be greater than zero.') }
  if (!isPositive(input.strike)) { errors.push('Strike price must be greater than zero.') }
  if (!isPositive(input.premium)) { errors.push('Premium must be greater than zero.') }
  if (!isPositive(input.quantity)) { errors.push('Quantity must be greater than zero.') }
  if (input.costs === null || input.costs === undefined) { errors.push('Costs must be specified (0 if none).') }
  if (typeof input.costs === 'number' && !isNonNegative(input.costs)) { errors.push('Costs cannot be negative.') }
  if (!hasValue(ACTION, input.action)) { errors.push('Invalid action (buy/sell).') }
  if (!hasValue(TYPE, input.type)) { errors.push('Invalid type (call/put).') }
  if (businessDays(input.baseDate, input.expirationDate) < 1) {
    errors.push('Expiration date must be after base date (at least 1 business day).')
  }
  return errors
}

const ratio = (numerator, denominator) => numerator / denominator

function capitalFor (input) {
  const { action, type, spot, strike, premium, quantity, costs } = input
  const gross = premium * quantity
  if (action === ACTION.BUY) { return gross + costs }
  if (type === TYPE.PUT) { return strike * quantity }
  return spot * quantity - (gross - costs)
}

function breakEvenFor (input, netPremium, totalCost) {
  const { action, type, spot, strike, quantity } = input
  if (action === ACTION.BUY) {
    return type === TYPE.CALL ? strike + totalCost / quantity : strike - totalCost / quantity
  }
  return type === TYPE.CALL ? spot - netPremium / quantity : strike - netPremium / quantity
}

function ratesFor (input, netPremium, capital) {
  const isSellCall = input.action === ACTION.SELL && input.type === TYPE.CALL
  const isSell = input.action === ACTION.SELL
  return {
    staticRate: isSell ? ratio(netPremium, capital) : null,
    exerciseRate: isSellCall ? ratio(input.strike * input.quantity - capital, capital) : null
  }
}

const toCdiPct = (rate, period) => (rate === null || period === null || period === 0 ? null : rate / period)

export function simulate (input) {
  const errors = validateSimulation(input)
  if (errors.length > 0) { return { valid: false, errors } }

  const { action, premium, quantity, costs, baseDate, expirationDate, cdiAnnual, type, spot, strike } = input
  const du = businessDays(baseDate, expirationDate)
  const gross = premium * quantity
  const isSell = action === ACTION.SELL
  const netPremium = gross - costs
  const totalCost = gross + costs

  const allocatedCapital = capitalFor(input)
  const { staticRate, exerciseRate } = ratesFor(input, netPremium, allocatedCapital)
  const periodCdi = cdiPeriod(cdiAnnual ?? null, du)
  const position = moneyness(type, spot, strike)

  return {
    valid: true,
    errors,
    du,
    netVolume: isSell ? netPremium : -totalCost,
    allocatedCapital,
    staticRate,
    exerciseRate,
    staticRateAnnual: annualize(staticRate, du),
    exerciseRateAnnual: annualize(exerciseRate, du),
    cdiPeriod: periodCdi,
    staticRateCdiPct: toCdiPct(staticRate, periodCdi),
    exerciseRateCdiPct: toCdiPct(exerciseRate, periodCdi),
    breakEven: breakEvenFor(input, netPremium, totalCost),
    maxLoss: isSell ? null : totalCost,
    distance: position.distance,
    moneyness: position.status
  }
}

/** Draft object consumed by Order.vue (prop `draft`). */
export function buildOrderDraft (input) {
  return {
    symbol: input.symbol,
    symbolAggregate: input.symbolAggregate,
    action: input.action,
    type: input.type,
    quantity: input.quantity,
    price: input.premium,
    strikePrice: input.strike,
    expirationDate: input.expirationDate,
    comission: input.costs,
    otherCost: 0,
    isOption: true
  }
}

const CAPTURE_ALERT_THRESHOLD = 0.8
const OPTION_TYPES = [TYPE.PUT, TYPE.CALL]

/** D3: only complete option orders can be tracked. */
export function isTrackable (order) {
  if (!order || !order.isOption) { return false }
  if (!OPTION_TYPES.includes(order.type)) { return false }
  if (!toLocalDate(order.expirationDate)) { return false }
  return isPositive(order.strikePrice) && isPositive(order.price) && isPositive(order.quantity)
}

export function validateTracking (input) {
  const errors = []
  if (!input) { return ['Tracking data not provided.'] }

  if (!isPositive(input.currentSpot)) { errors.push('Current underlying asset price must be greater than zero.') }
  if (!isNonNegative(input.currentPremium)) { errors.push('Current premium must be specified (0 if option is worthless).') }
  if (input.openSpot !== null && input.openSpot !== undefined && !isPositive(input.openSpot)) {
    errors.push('Opening underlying price, if specified, must be greater than zero.')
  }

  const baseDate = toLocalDate(input.baseDate)
  if (!baseDate) {
    errors.push('Invalid base date.')
    return errors
  }

  const openDate = toLocalDate(input.openDate)
  if (openDate && baseDate < openDate) { errors.push('Base date cannot be earlier than order opening date.') }
  return errors
}

const intrinsicValue = (type, spot, strike) => (
  type === TYPE.CALL ? Math.max(spot - strike, 0) : Math.max(strike - spot, 0)
)

function openExtrinsicFor (order, openSpot, currentExtrinsic) {
  if (!isPositive(openSpot)) { return { openIntrinsic: null, openExtrinsic: null, extrinsicDecay: null } }

  const openIntrinsic = intrinsicValue(order.type, openSpot, order.strikePrice)
  const openExtrinsic = order.price - openIntrinsic
  return { openIntrinsic, openExtrinsic, extrinsicDecay: openExtrinsic - currentExtrinsic }
}

function remainingFor (order, input, daysRemaining, expired) {
  const none = { currentCapital: null, remainingRate: null, remainingRateAnnual: null, cdiPeriod: null, remainingRateCdiPct: null }
  if (order.action !== ACTION.SELL || expired) { return none }

  const { currentSpot, currentPremium, cdiAnnual } = input
  const gross = currentPremium * order.quantity
  const currentCapital = order.type === TYPE.PUT
    ? order.strikePrice * order.quantity
    : currentSpot * order.quantity - gross
  const remainingRate = currentCapital > 0 ? ratio(gross, currentCapital) : null
  const periodCdi = cdiPeriod(cdiAnnual ?? null, daysRemaining)

  return {
    currentCapital,
    remainingRate,
    remainingRateAnnual: annualize(remainingRate, daysRemaining),
    cdiPeriod: periodCdi,
    remainingRateCdiPct: toCdiPct(remainingRate, periodCdi)
  }
}

export function track (order, input) {
  if (!isTrackable(order)) { return { valid: false, errors: ['Order does not have required data for tracking.'] } }

  const errors = validateTracking({ ...input, openDate: order.date })
  if (errors.length > 0) { return { valid: false, errors } }

  const { currentSpot, currentPremium, baseDate, openSpot } = input
  const quantity = Math.abs(order.quantity)
  const costs = (order.comission || 0) + (order.otherCost || 0)
  const isSell = order.action === ACTION.SELL
  const priceDelta = isSell ? order.price - currentPremium : currentPremium - order.price

  const intrinsic = intrinsicValue(order.type, currentSpot, order.strikePrice)
  const extrinsic = currentPremium - intrinsic
  const daysElapsed = businessDays(order.date, baseDate)
  const daysRemaining = businessDays(baseDate, order.expirationDate)
  const daysTotal = daysElapsed + daysRemaining
  const expired = toLocalDate(baseDate) >= toLocalDate(order.expirationDate)
  const pnlPct = ratio(priceDelta, order.price)

  return {
    valid: true,
    errors,
    isSell,
    pnl: priceDelta * quantity - costs,
    pnlPct,
    intrinsic,
    extrinsic,
    extrinsicNegative: extrinsic < 0,
    ...openExtrinsicFor(order, openSpot, extrinsic),
    daysElapsed,
    daysRemaining,
    daysTotal,
    progress: daysTotal > 0 ? daysElapsed / daysTotal : 1,
    expired,
    ...remainingFor({ ...order, quantity }, input, daysRemaining, expired),
    captureAlert: isSell && pnlPct >= CAPTURE_ALERT_THRESHOLD
  }
}

/** Draft object (closing order) consumed by Order.vue (prop `draft`). */
export function buildCloseOrderDraft (order, input) {
  return {
    symbol: order.symbol,
    symbolAggregate: order.symbolAggregate,
    type: order.type,
    strikePrice: order.strikePrice,
    expirationDate: order.expirationDate,
    accountNumber: order.accountNumber,
    portfolio: order.portfolio,
    quantity: order.quantity,
    isOption: true,
    action: order.action === ACTION.SELL ? ACTION.BUY : ACTION.SELL,
    price: input.currentPremium,
    date: toLocalDate(input.baseDate),
    comission: 0,
    otherCost: 0
  }
}
